import {Node} from '@wandb/weave/core';
import {useNodeValue} from '@wandb/weave/react';
import {produce} from 'immer';
import {useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {
  FetchRegistryArtifactCollectionsPartialQueryResult,
  Maybe,
  RegistrySearchCollectionFragmentFragment,
  RegistrySearchResultFragmentFragment,
  useFetchArtifactCollectionTriggersQuery,
  useFetchProjectTriggersQuery,
  useFetchRegistryArtifactCollectionsPartialQuery,
} from '../../generated/graphql';
import {propagateErrorsContext} from '../../util/errors';
import {isUserVisibleTrigger} from '../../util/triggers';
import {sliceArray} from '../Pagination';
import {SingleRegistrySearchContext} from '../Registries/common/RegistriesSearchContextProvider';
import {
  GQLArtifactCollectionSearchEdgeType,
  RegistrySearchCollectionType,
} from '../Registries/common/types';
import {
  FetchArtifactCollectionsResult,
  parseRegistrySearchCollections,
} from '../Registries/RegistriesHomepage/hooks';
import {CollectionType} from '../Registries/Registry/types';
import {createDisplayTag, mapGQLTags, matchPartialTag} from '../TagAssignment';
import {
  artifactsNodeToPathDictNode,
  DEFAULT_PROJECT_NAME,
  sortByArtifactMembershipDates,
} from './utils';

/**
 * Fetch all model registry triggers for a team or all teams in an org
 */
export const useTeamTriggers = (entityName: string | undefined) => {
  const projectTriggersQuery = useFetchProjectTriggersQuery({
    variables: {
      projectName: DEFAULT_PROJECT_NAME,
      entityName: entityName ?? '',
      projectOnly: true,
    },
    context: propagateErrorsContext(),
    skip: !entityName,
  });

  const ret = useMemo(() => {
    const triggers = projectTriggersQuery.data?.project?.triggers ?? [];
    return {triggers, projectTriggersQuery};
  }, [projectTriggersQuery]);
  return ret;
};

/**
 * Triggers information about a model
 */
export const useTriggers = (
  entityName: string,
  projectName: string,
  collectionID: string
) => {
  const projectTriggersQuery = useFetchProjectTriggersQuery({
    variables: {
      projectName,
      entityName,
    },
    skip: !entityName || !projectName,
    context: propagateErrorsContext(),
  });
  const collectionTriggersQuery = useFetchArtifactCollectionTriggersQuery({
    variables: {
      artifactCollectionID: collectionID,
    },
    skip: !collectionID,
    context: propagateErrorsContext(),
  });
  const projectScopedTriggers =
    projectTriggersQuery?.data?.project?.triggers
      .filter(t => t.scope.__typename === 'Project')
      .filter(
        t =>
          t.triggeredAction.__typename === 'QueueJobTriggeredAction' ||
          t.triggeredAction.__typename === 'GenericWebhookTriggeredAction' ||
          t.triggeredAction.__typename === 'NotificationTriggeredAction'
      ) ?? [];
  const acScopedTriggers =
    collectionTriggersQuery?.data?.artifactCollection?.triggers.edges
      .map(t => {
        return t?.node;
      })
      .filter(
        t =>
          t.triggeredAction.__typename === 'QueueJobTriggeredAction' ||
          t.triggeredAction.__typename === 'GenericWebhookTriggeredAction' ||
          t.triggeredAction.__typename === 'NotificationTriggeredAction'
      ) ?? [];
  const triggers = [...acScopedTriggers, ...projectScopedTriggers].filter(
    isUserVisibleTrigger
  );
  return {
    triggers,
    loading: projectTriggersQuery.loading && collectionTriggersQuery.loading,
    error: projectTriggersQuery.error
      ? projectTriggersQuery.error
      : collectionTriggersQuery.error,
  };
};

/**
 * Filter artifact collection nodes based on search string
 * using Entity name, project name, model name, and tags
 */
export const useFilterCollectionNodesBasedOnSearchString = (
  collectionNodes: Node<{type: 'list'; objectType: 'artifact'}>,
  searchFilter?: string
) => {
  const filteredCollections: CollectionType[] = [];

  const pathDictValue = useNodeValue(
    useMemo(() => {
      return artifactsNodeToPathDictNode(collectionNodes as any);
    }, [collectionNodes])
  );

  if (pathDictValue.loading) {
    return {loading: true, result: []};
  }
  const searchTarget = searchFilter?.toLowerCase();
  pathDictValue.result.artifactCollections?.forEach((collection: any) => {
    const currentTagsRaw: Array<{
      id: string;
      name: string;
      tagCategoryName: string;
      attributes: string;
    }> = collection.rawTags ?? [];
    const currentTags = mapGQLTags(currentTagsRaw);

    const currentVersionTagsRaw: Array<{
      id: string;
      name: string;
      tagCategoryName: string;
      attributes: string;
    }> = collection.rawVersionTags?.flat() ?? [];
    const currentVersionTags = mapGQLTags(currentVersionTagsRaw);

    if (
      searchTarget == null ||
      searchTarget === '' ||
      collection.collectionName.toLowerCase().includes(searchTarget) ||
      collection.entityName.toLowerCase().includes(searchTarget) ||
      // Not searching against projectName here as it might cause confusion
      //  because in the model registry, the project = model-registry
      currentTags.some(tag => matchPartialTag(tag, searchTarget)) ||
      currentVersionTags.some(versionTag =>
        matchPartialTag(versionTag, searchTarget)
      )
    ) {
      const tagsList = currentVersionTags.map(tag => createDisplayTag(tag));
      filteredCollections.push({
        ...collection,
        tags: tagsList,
        collectionTags: currentTags,
      });
    }
  });
  return {
    loading: false,
    result: filteredCollections.sort((a, b) =>
      sortByArtifactMembershipDates(
        a.createdAt,
        b.createdAt,
        a.lastMembershipCreatedAt,
        b.lastMembershipCreatedAt
      )
    ),
  };
};

/**
 * filters out collections whose types don't have 'model' in their names
 * For parity with the current data that the model registry shows
 */
type FilterableArtifactCollectionSearchEdge = {
  node: RegistrySearchCollectionFragmentFragment;
};
const isModelTypeCollectionEdge = (
  edge: FilterableArtifactCollectionSearchEdge
): boolean => {
  return edge.node.defaultArtifactType.name.toLowerCase().includes('model');
};

function getModelRegistryCollectionsSearchResult(
  data: FetchRegistryArtifactCollectionsPartialQueryResult['data']
): Maybe<RegistrySearchResultFragmentFragment> {
  return data?.project?.artifactCollectionsSearch ?? null;
}

type ModelRegistryArtifactCollectionsHookProps = {
  entityName: string;
  pageSize: number;
};

export const useSearchablePaginatedModelRegistryArtifactCollections = ({
  entityName,
  pageSize,
}: ModelRegistryArtifactCollectionsHookProps): FetchArtifactCollectionsResult => {
  // TODO: parameterize this to reduce duplicated code
  const getSearchResult = getModelRegistryCollectionsSearchResult;

  const {searchFilter} = useContext(SingleRegistrySearchContext);
  const [page, setPage] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<
    RegistrySearchCollectionType[]
  >([]);

  // In the backend, fetching collections without member artifacts is reasonably fast, so we can fetch a larger batch on page load.  However, search could use further optimization.
  // For now, use a smaller batch size when there's a non-empty search term so the user can see results faster.
  const {refetch, fetchMore, data, loading} =
    useFetchRegistryArtifactCollectionsPartialQuery({
      variables: {
        entityName,
        projectName: DEFAULT_PROJECT_NAME,
        searchTerm: searchFilter,
        limit: searchFilter ? pageSize : 100,
        artifactsLimit: 0,
      },
      onCompleted: data => {
        // No analytics tracking for now, for parity with existing model registry behavior
      },
      context: propagateErrorsContext(),
      // This updates data whenever fetchMore is called and results in
      // duplicate queries being made, so we need to make sure we don't
      // append the same data twice in updateQuery
      notifyOnNetworkStatusChange: true,
    });

  const fetchMoreCollections = useCallback(
    (cursor: string) => {
      fetchMore({
        variables: {
          entityName,
          searchTerm: searchFilter,
          cursor,
          limit: pageSize,
          artifactsLimit: 0,
        },
        updateQuery: (prev, {fetchMoreResult}) => {
          const searchResult = getSearchResult(fetchMoreResult);
          if (!searchResult) {
            return prev;
          }
          return produce(prev, draft => {
            const draftSearchResult = getSearchResult(draft);
            if (
              draftSearchResult &&
              draftSearchResult.pageInfo.endCursor !==
                searchResult.pageInfo?.endCursor
            ) {
              draftSearchResult.pageInfo = searchResult.pageInfo;
              draftSearchResult.edges.push(...searchResult.edges);
            }
          });
        },
      });
    },
    [fetchMore, entityName, searchFilter, getSearchResult, pageSize]
  );

  const {edges, pageInfo} = useMemo(() => {
    const searchResult = getSearchResult(data);
    return {
      edges: searchResult?.edges.filter(isModelTypeCollectionEdge) ?? [],
      pageInfo: searchResult?.pageInfo,
    };
  }, [data, getSearchResult]);

  useEffect(() => {
    // check if we need to fetch more data for the current page
    if (edges.length < (page + 1) * pageSize && pageInfo?.hasNextPage) {
      fetchMoreCollections(pageInfo?.endCursor ?? '');
    } else {
      // if we don't need to fetch more, set the current page
      const currentCollections: RegistrySearchCollectionType[] =
        parseRegistrySearchCollections(sliceArray(edges, page, pageSize));
      setCurrentPage(currentCollections);
    }
  }, [edges, pageInfo, page, pageSize, fetchMoreCollections]);

  // reset the view to the first page if the search filter changes
  useEffect(() => {
    setPage(0);
  }, [searchFilter]);

  return {
    currentPage: currentPage,
    loading: loading,
    refetch: refetch,
    totalCollectionsLoaded: edges?.length ?? 0,
    onPageChange: setPage,
    page: page,
    hasNextPage: pageInfo?.hasNextPage ?? false,
  };
};

// Incremental, one-off fetch of all model registry artifact collections without member artifacts
// Needed to maintain the behavior of CreateRegisteredCollectionDrawer
export const useAllModelRegistryArtifactCollectionsWithoutMembers = ({
  entityName,
}: {
  entityName: string;
}): {
  collections: RegistrySearchCollectionType[];
  loading: boolean;
  refetch: () => void;
} => {
  // TODO: parameterize this to reduce duplicated code
  const getSearchResult = getModelRegistryCollectionsSearchResult;
  const [collections, setCollections] = useState<
    RegistrySearchCollectionType[]
  >([]);

  const queryVariables = useMemo(
    () => ({
      entityName,
      projectName: DEFAULT_PROJECT_NAME,
      searchTerm: '',
      limit: 100,
      artifactsLimit: 0,
    }),
    [entityName]
  );

  const {refetch, fetchMore, data, loading, error} =
    useFetchRegistryArtifactCollectionsPartialQuery({
      variables: queryVariables,
      context: propagateErrorsContext(),
    });

  const fetchMoreCollections = useCallback(
    (cursor: string) => {
      fetchMore({
        variables: {...queryVariables, cursor},
        updateQuery: (prev, {fetchMoreResult}) => {
          const searchResult = getSearchResult(fetchMoreResult);
          if (!searchResult) {
            return prev;
          }
          return produce(prev, draft => {
            const draftSearchResult = getSearchResult(draft);
            if (draftSearchResult) {
              draftSearchResult.pageInfo = searchResult.pageInfo;
              draftSearchResult.edges.push(...searchResult.edges);
            }
          });
        },
      });
    },
    [fetchMore, getSearchResult, queryVariables]
  );

  const {edges, pageInfo} = useMemo(() => {
    const searchResult = getSearchResult(data);
    return {
      edges: searchResult?.edges.filter(isModelTypeCollectionEdge),
      pageInfo: searchResult?.pageInfo,
    };
  }, [data, getSearchResult]);

  useEffect(() => {
    // check if we need to fetch more data to gather all collections
    if (pageInfo?.hasNextPage) {
      fetchMoreCollections(pageInfo?.endCursor ?? '');
    } else {
      // if we don't need to fetch more, filter and parse
      const filteredEdges = edges?.filter(isModelTypeCollectionEdge) ?? [];
      const parsedCollections: RegistrySearchCollectionType[] =
        parseRegistrySearchCollections(
          filteredEdges as GQLArtifactCollectionSearchEdgeType[]
        );
      setCollections(parsedCollections);
    }
  }, [edges, pageInfo, fetchMoreCollections]);

  return {collections, loading, refetch};
};
